import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { PartnerShell, Kicker } from '../../components/partner/PartnerShell';

/**
 * Partner Link dashboard — Jenn, 2026-10-07. Partners (therapists and
 * practitioners) are not members, so this page stands on its own: no Hub
 * menu, its own sign-in, and its own token (never the member login).
 * Partners see their link, their page status, their choices, and
 * counts/money only — never who joined.
 *
 * Password sign-in (2026-10-12): email + a password they choose; the emailed
 * link (?setpassword=) is only for choosing or resetting it. Signed out after
 * 30 minutes with no activity and at the end of the day (the server enforces
 * both; this page also clears the screen so client names don't stay showing).
 */

const partnerApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});
const TOKEN_KEY = 'partnerToken';
const readToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const saveToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ } };

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Shell = ({ children, right }) => <PartnerShell right={right}>{children}</PartnerShell>;

function Card({ title, kicker, children }) {
  return (
    <section className="bg-white rounded-2xl shadow-sm p-6 mb-8">
      {kicker && <Kicker>{kicker}</Kicker>}
      {title && <h2 className="text-lg font-bold text-reset-brown mb-3">{title}</h2>}
      {children}
    </section>
  );
}

const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-reset-terracotta';
const buttonClass = 'px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full disabled:opacity-50';

function PasswordLinkForm({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await partnerApi.post('/partner/password-link', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <p className="text-reset-brown">
        If that email belongs to an approved partner, a link to choose your password is on its way. It works for 30 minutes.
        Check your spam folder if you don't see it.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-reset-brown">
        First time here, or forgot your password? Enter the email you used when you applied, and we'll email you a link to choose your password.
      </p>
      <label htmlFor="partner-link-email" className="block text-sm font-medium text-reset-brown">Email</label>
      <input id="partner-link-email" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" disabled={busy} className={buttonClass}>{busy ? 'Sending…' : 'Email me a link'}</button>
        <button type="button" onClick={onBack} className="text-sm text-reset-brown underline">Back to sign in</button>
      </div>
      {error && <p className="text-sm text-reset-terracotta">{error}</p>}
    </form>
  );
}

function SignIn({ notice, onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [wantLink, setWantLink] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await partnerApi.post('/partner/sign-in', { email, password });
      setPassword('');
      onSignedIn(r.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-serif text-3xl font-bold text-reset-brown mb-2">Your partner dashboard</h1>
      <p className="text-reset-grey mb-6">Sign in with the email you used when you applied and the password you chose.</p>
      {notice && <p className="text-sm bg-reset-creamDark text-reset-brown rounded-xl px-4 py-3 mb-4">{notice}</p>}
      <Card>
        {wantLink ? <PasswordLinkForm onBack={() => setWantLink(false)} /> : (
          <form onSubmit={submit} className="space-y-4">
            <label htmlFor="partner-email" className="block text-sm font-medium text-reset-brown">Email</label>
            <input id="partner-email" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
            <label htmlFor="partner-password" className="block text-sm font-medium text-reset-brown">Password</label>
            <input id="partner-password" type="password" required autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} className={inputClass} />
            <button type="submit" disabled={busy} className={buttonClass}>{busy ? 'Signing in…' : 'Sign in'}</button>
            {error && <p className="text-sm text-reset-terracotta">{error}</p>}
            <p className="text-sm text-reset-brown pt-2">
              First time here, or forgot your password?{' '}
              <button type="button" onClick={() => setWantLink(true)} className="font-bold text-reset-terracotta underline">Email me a link</button>
            </p>
          </form>
        )}
      </Card>
      <p className="text-sm text-reset-grey">
        Not a partner yet? <a href="https://365reset.com/partner-link.html" className="text-reset-terracotta font-bold underline">Learn about the Partner Link</a>
      </p>
    </Shell>
  );
}

function ChoosePassword({ linkToken, onSignedIn, onCancel }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Please use at least 8 characters.');
    if (password !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      const r = await partnerApi.post('/partner/set-password', { token: linkToken, password });
      onSignedIn(r.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-serif text-3xl font-bold text-reset-brown mb-2">Choose your password</h1>
      <p className="text-reset-grey mb-6">At least 8 characters. From now on you'll sign in with your email and this password.</p>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          <label htmlFor="partner-new-password" className="block text-sm font-medium text-reset-brown">Password</label>
          <input id="partner-new-password" type="password" required autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} className={inputClass} />
          <label htmlFor="partner-confirm-password" className="block text-sm font-medium text-reset-brown">Type it again</label>
          <input id="partner-confirm-password" type="password" required autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} className={inputClass} />
          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" disabled={busy} className={buttonClass}>{busy ? 'Saving…' : 'Save my password'}</button>
            <button type="button" onClick={onCancel} className="text-sm text-reset-brown underline">Back to sign in</button>
          </div>
          {error && <p className="text-sm text-reset-terracotta">{error}</p>}
        </form>
      </Card>
    </Shell>
  );
}

function Settings({ data, onSaved, token }) {
  const p = data.partner;
  const [give, setGive] = useState(!!p.give_to_clients);
  const [inCanada, setInCanada] = useState(p.in_canada);
  const [method, setMethod] = useState(p.payout_method || '');
  const [payEmail, setPayEmail] = useState(p.payout_email || p.email || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Outside Canada it's PayPal only, so pick it for them.
  useEffect(() => { if (inCanada === false && method !== 'paypal') setMethod('paypal'); }, [inCanada, method]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const r = await partnerApi.put('/partner/me',
        { giveToClients: give, inCanada, payoutMethod: method || null, payoutEmail: payEmail },
        { headers: { Authorization: `Bearer ${token}` } });
      onSaved(r.data);
      setMsg({ ok: true, text: 'Saved.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Could not save. Please try again.' });
    }
    setBusy(false);
  };

  const radio = 'mt-1 h-4 w-4 accent-reset-terracotta';

  return (
    <Card kicker="Your 10%">
      <form onSubmit={save} className="space-y-5">
        <fieldset className="space-y-3">
          <legend className="text-sm text-reset-grey mb-2">For every person who joins through your link, you can:</legend>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="radio" name="give" className={radio} checked={!give} onChange={() => setGive(false)} />
            <span className="text-sm text-reset-brown"><strong>Take it.</strong> We pay you 10% of each of their monthly payments, for as long as they stay. Paid once a month.</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="radio" name="give" className={radio} checked={give} onChange={() => setGive(true)} />
            <span className="text-sm text-reset-brown"><strong>Give it to your clients.</strong> People who join through your link get 10% off every payment instead, and you aren't paid for them.</span>
          </label>
          <p className="text-xs text-reset-grey">If you change this later, it only applies to people who join after the change.</p>
        </fieldset>

        {!give && (
          <div className="space-y-4 border-t border-reset-creamDark pt-5">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-reset-brown mb-1">Do you live in Canada?</legend>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="canada" className={radio} checked={inCanada === true} onChange={() => setInCanada(true)} />
                <span className="text-sm text-reset-brown">Yes</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="canada" className={radio} checked={inCanada === false} onChange={() => setInCanada(false)} />
                <span className="text-sm text-reset-brown">No</span>
              </label>
            </fieldset>
            {inCanada !== null && inCanada !== undefined && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-reset-brown mb-1">How would you like to be paid?</legend>
                {inCanada && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="radio" name="method" className={radio} checked={method === 'etransfer'} onChange={() => setMethod('etransfer')} />
                    <span className="text-sm text-reset-brown">Interac e-Transfer</span>
                  </label>
                )}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="method" className={radio} checked={method === 'paypal'} onChange={() => setMethod('paypal')} />
                  <span className="text-sm text-reset-brown">PayPal</span>
                </label>
                {!inCanada && <p className="text-xs text-reset-grey">Outside Canada, we pay by PayPal.</p>}
              </fieldset>
            )}
            <div>
              <label htmlFor="pay-email" className="block text-sm font-medium text-reset-brown mb-1.5">
                Email to send your e-Transfer or PayPal to
              </label>
              <input id="pay-email" type="email" value={payEmail} onChange={e => setPayEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-reset-terracotta" />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy}
            className="px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full disabled:opacity-50">
            {busy ? 'Saving…' : 'Save my choices'}
          </button>
          {msg && <span className={`text-sm ${msg.ok ? 'text-reset-sage' : 'text-reset-terracotta'}`}>{msg.text}</span>}
        </div>
      </form>
    </Card>
  );
}

function CopyButton({ text, label = 'Copy', dark = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <button type="button" onClick={copy}
      className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors shrink-0 ${dark ? 'bg-reset-gold text-reset-brownDeep' : 'bg-reset-terracotta text-white hover:bg-reset-brown'}`}>
      {copied ? 'Copied!' : label}
    </button>
  );
}

function StatCard({ label, value, children, dark = false }) {
  return (
    <div className={`rounded-2xl p-6 shadow-sm ${dark ? 'bg-gradient-to-br from-reset-sage to-[#3b5245] text-reset-cream' : 'bg-white'}`}>
      <p className={`text-xs font-bold mb-2 ${dark ? 'text-reset-gold' : 'text-reset-sage'}`}>{label}</p>
      <p className={`font-serif text-3xl leading-none ${dark ? 'text-reset-cream' : 'text-reset-brownDeep'}`}>{value}</p>
      {children && <p className={`text-sm mt-2 ${dark ? 'text-reset-cream/75' : 'text-gray-500'}`}>{children}</p>}
    </div>
  );
}

const SOCIAL = [['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn']];
const MATERIAL_TABS = [['social', 'Social media posts'], ['email', 'Email to your clients'], ['newsletter', 'Newsletter'], ['brochure', 'Printable brochure']];

const escapeHtml = (t) => String(t || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// The brochure opens as its own printable page — "Print, or save as PDF".
function openBrochure({ text, partner, link, qr }) {
  const [heading, ...rest] = String(text || '').split('\n');
  const body = rest.join('\n').trim().split(/\n{2,}/).map(block => {
    const items = block.split('\n');
    return items.every(l => l.trim().startsWith('- '))
      ? `<ul>${items.map(l => `<li>${escapeHtml(l.trim().slice(2))}</li>`).join('')}</ul>`
      : `<p>${items.map(escapeHtml).join('<br>')}</p>`;
  }).join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>365reset brochure</title>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;1,500&family=DM+Sans:wght@400;700&display=swap" rel="stylesheet">
<style>
  @page { size: letter; margin: 0.6in; }
  body { font-family: 'DM Sans', sans-serif; color: #2B2313; margin: 0; background: #fff; }
  .page { max-width: 7.3in; margin: 0 auto; }
  .bar { background: #2C2416; color: #FAF7F2; padding: 18px 24px; border-radius: 14px; display: flex; justify-content: space-between; align-items: center; }
  .mark { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 30px; }
  .tag { color: #DEC392; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
  h1 { font-family: 'Cormorant Garamond', serif; font-weight: 600; font-size: 34px; line-height: 1.15; margin: 28px 0 12px; }
  p, li { font-size: 14.5px; line-height: 1.6; }
  ul { padding-left: 20px; } li { margin: 4px 0; }
  .from { margin-top: 26px; padding: 20px 24px; background: #F0EBE3; border-radius: 14px; display: flex; gap: 24px; align-items: center; }
  .from img { width: 130px; height: 130px; }
  .who { font-weight: 700; font-size: 16px; } .practice { color: #4A4A4A; font-size: 13px; } .url { color: #C4714A; font-size: 12px; word-break: break-all; margin-top: 8px; }
  .print { margin: 20px auto; display: block; padding: 10px 22px; border: 0; border-radius: 999px; background: #C4714A; color: #fff; font-weight: 700; font-size: 14px; cursor: pointer; }
  @media print { .print { display: none; } }
</style></head><body><div class="page">
<button class="print" onclick="window.print()">Print, or save as PDF</button>
<div class="bar"><span class="mark">365reset</span><span class="tag">Recommended by ${escapeHtml(partner.name)}</span></div>
<h1>${escapeHtml(heading)}</h1>
${body}
<div class="from">${qr ? `<img src="${qr}" alt="QR code to ${escapeHtml(partner.name)}'s 365reset page">` : ''}
<div><div class="who">${escapeHtml(partner.name)}</div>${partner.practice_name ? `<div class="practice">${escapeHtml(partner.practice_name)}</div>` : ''}
${link ? `<div class="url">${escapeHtml(link)}</div>` : ''}</div></div>
</div></body></html>`);
  w.document.close();
}

function Materials({ materials, link, partner, qr }) {
  const [tab, setTab] = useState('social');
  const [social, setSocial] = useState('facebook');
  const withLink = (t) => (link ? `${t}\n\n${link}` : t);
  const cards = (list, copyLabel) => (list?.length ? (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {list.map((body, i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm p-5 flex flex-col">
          <p className="text-sm text-reset-brown whitespace-pre-line leading-relaxed mb-4">{body}</p>
          <div className="mt-auto"><CopyButton text={withLink(body)} label={copyLabel} /></div>
        </div>
      ))}
    </div>
  ) : <p className="text-sm text-gray-400">Nothing here yet. Check back soon.</p>);

  return (
    <div className="mb-8">
      <Kicker>Share 365reset with your clients</Kicker>
      <p className="text-sm text-gray-500 mb-4 max-w-2xl">
        Ready-made words you can use as they are, or change to sound like you.
        {link ? ' Your link is added automatically when you copy.' : ' Your link gets added here once your page is live.'}
      </p>
      <div className="flex gap-2 flex-wrap mb-5">
        {MATERIAL_TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === key ? 'bg-reset-terracotta text-white' : 'bg-reset-creamDark text-reset-brown hover:bg-reset-gold/30'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'social' && (
        <>
          <div className="flex gap-2 flex-wrap mb-4">
            {SOCIAL.map(([key, label]) => (
              <button key={key} onClick={() => setSocial(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${social === key ? 'border-reset-brownDeep bg-reset-brownDeep text-reset-cream' : 'border-black/10 bg-white text-reset-brown'}`}>
                {label}
              </button>
            ))}
          </div>
          {cards(materials?.[social], 'Copy post')}
        </>
      )}
      {tab === 'email' && cards(materials?.email, 'Copy email')}
      {tab === 'newsletter' && cards(materials?.newsletter, 'Copy for newsletter')}
      {tab === 'brochure' && (
        materials?.brochure?.length ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-start">
            <div>
              <p className="text-sm text-reset-brown whitespace-pre-line leading-relaxed">{materials.brochure[0]}</p>
            </div>
            <div className="bg-reset-cream rounded-xl p-5 text-center">
              {qr ? <img src={qr} alt={`QR code to ${partner.name}'s 365reset page`} className="w-36 h-36 mx-auto mb-3" />
                  : <div className="w-36 h-36 mx-auto mb-3 rounded-lg border border-dashed border-reset-terracotta/40 flex items-center justify-center text-xs text-gray-400 px-3">Your QR code appears once your page is live</div>}
              <p className="text-xs text-gray-500 mb-4">A one-page handout with your name and a QR code that opens your page.</p>
              <button onClick={() => openBrochure({ text: materials.brochure[0], partner, link, qr })} disabled={!link}
                className="px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full disabled:opacity-40">
                Open brochure to print
              </button>
            </div>
          </div>
        ) : <p className="text-sm text-gray-400">Nothing here yet. Check back soon.</p>
      )}
    </div>
  );
}

function Dashboard({ data, setData, token, signOut }) {
  const { partner, page, referrals } = data;
  const first = (partner.name || '').split(' ')[0];

  return (
    <Shell right={<button onClick={signOut} className="text-xs font-bold text-reset-cream/80 underline">Sign out</button>}>
      <div className="mb-8">
        <Kicker>Welcome, {first}</Kicker>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-reset-brownDeep leading-tight max-w-2xl">
          Everything you need to share 365reset with your clients, and see exactly <em className="italic text-reset-terracotta">where it stands</em>.
        </h1>
        {partner.practice_name && <p className="text-sm text-gray-500 mt-2">{partner.practice_name}</p>}
      </div>

      {page.status === 'not_started' && (
        <div className="rounded-2xl bg-reset-creamDark border-l-[3px] border-reset-terracotta p-7 mb-8">
          <Kicker>Your next step</Kicker>
          <p className="text-sm text-reset-brown mb-4 max-w-xl">
            We make every partner their own 365reset page to share with clients. Tell us a little about you and your practice
            (about 10 minutes) and we'll write it for you, then send it to you for a look before it goes live.
          </p>
          {page.form_url && <a href={page.form_url} className="inline-block px-5 py-2.5 bg-reset-terracotta text-white text-sm font-semibold rounded-full hover:bg-reset-brown transition-colors">Tell us about you →</a>}
        </div>
      )}
      {page.status === 'waiting' && (
        <div className="rounded-2xl bg-reset-creamDark border-l-[3px] border-reset-sage p-7 mb-8">
          <Kicker className="text-reset-sage">In review</Kicker>
          <p className="text-sm text-reset-brown max-w-xl">
            Thank you, we have everything. Jenn is writing and checking your page now. Your link turns on as soon as it's approved, and we'll email you.
          </p>
        </div>
      )}

      <div className="rounded-2xl p-7 mb-8 shadow-sm bg-gradient-to-br from-reset-gold/35 via-reset-creamDark to-reset-creamDark grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-7 items-center">
        <div>
          <Kicker className="text-reset-sage">Your link</Kicker>
          {page.link ? (
            <div className="flex gap-2 items-center bg-white rounded-xl px-3 py-2 shadow-sm">
              <span className="text-sm text-reset-brown truncate flex-1">{page.link}</span>
              <CopyButton text={page.link} label="Copy link" />
            </div>
          ) : (
            <p className="text-sm text-reset-brown bg-white/70 rounded-xl px-3 py-2">Your link appears here once your page is approved.</p>
          )}
          <p className="text-sm text-gray-500 mt-3 mb-4 max-w-md">
            This link opens your own page. <strong className="text-reset-sage">Anyone who joins through it is connected to you</strong>, so we can pay your 10% or give it to them as a discount.
          </p>
          {page.link ? (
            <a href={page.link} target="_blank" rel="noopener noreferrer"
              className="inline-block px-5 py-2.5 border-[1.5px] border-reset-brownDeep text-reset-brownDeep text-sm font-bold rounded-full hover:bg-reset-brownDeep hover:text-reset-cream transition-colors">
              View your page
            </a>
          ) : (
            <span className="inline-block px-5 py-2.5 border-[1.5px] border-gray-300 text-gray-400 text-sm font-bold rounded-full cursor-default">Your page is on its way</span>
          )}
        </div>
        <div className="bg-reset-cream rounded-xl p-4 shadow-sm">
          <div className="aspect-video rounded-lg border border-dashed border-reset-terracotta/40 bg-gradient-to-br from-reset-terracotta/10 to-reset-sage/10 flex items-center justify-center text-center px-4">
            <p className="font-serif italic text-reset-terracotta text-sm">{page.link ? `${first}'s 365reset page` : 'Your personal page, coming soon'}</p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">Written from what you told us about your practice.</p>
            {(page.link || page.preview_url) && (
              <a href={page.link || page.preview_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-reset-terracotta shrink-0 ml-2">Preview →</a>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="Joined through you" value={referrals?.total || 0}>{referrals?.active || 0} still members.</StatCard>
        <StatCard label="To be paid" value={money(data.owed)}>{partner.give_to_clients ? 'You give your 10% to your clients.' : 'Paid to you once a month.'}</StatCard>
        <StatCard label="Paid to date" value={money(data.paid)} dark>Your total payments so far.</StatCard>
      </div>

      <Settings data={data} token={token} onSaved={setData} />

      <Materials materials={data.materials} link={page.link} partner={partner} qr={data.qr_data_url} />

      <div className="mb-8">
        <Kicker>Your clients</Kicker>
        <p className="text-sm text-gray-500 mb-3 max-w-2xl">You'll only see a name when that person chooses to share it with you, or to share their summaries. We never share their membership, payments or profile.</p>
        {!data.clients?.length ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-6 text-center text-sm text-gray-400">No one yet. People who join through your link will show here.</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Name</th>
                  <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Joined</th>
                  <th className="text-left font-bold text-gray-400 text-xs uppercase tracking-wide px-5 py-3">Sharing</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0 align-top">
                    <td className="px-5 py-3 text-reset-brown font-medium">{c.label}{!c.still_member && <span className="block text-xs text-gray-400 font-normal">No longer a member</span>}</td>
                    <td className="px-5 py-3 text-gray-500">{new Date(c.joined).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-gray-500">
                      {c.consented && c.share_email
                        ? <span>Summaries sent to <strong className="text-reset-brown">{c.share_email}</strong></span>
                        : c.consented
                        ? <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-reset-sage/15 text-reset-sage">Shares their name with you</span>
                        : <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-reset-creamDark text-reset-brown">Didn't consent to share</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mb-8">
        <Kicker>Your prospects</Kicker>
        <p className="text-sm text-gray-500 mb-3">Everyone who's clicked or looked, but hasn't joined yet.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          {[
            ['Clicked your link', data.prospects?.clicked],
            ['Viewed your page', data.prospects?.viewedLanding],
            ['Started signing up', data.prospects?.startedSignup],
            ['Joined', referrals?.total ?? 0],
          ].map(([label, value], i) => (
            <div key={label} className={`rounded-2xl p-5 text-center shadow-sm flex flex-col ${i === 3 ? 'bg-reset-sage text-reset-cream' : 'bg-white'}`}>
              <p className={`font-serif text-2xl ${i === 3 ? 'text-reset-cream' : 'text-reset-brownDeep'}`}>{value ?? 0}</p>
              <p className={`text-xs mt-1 ${i === 3 ? 'text-reset-cream/80' : 'text-gray-500'}`}>{label}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 text-center">Each person is counted once, however many times they visit. "Joined" counts everyone who has ever joined through your link.</p>
      </div>

      <div className="mb-8">
        <Kicker>Recent commissions</Kicker>
        {!data.commissions?.length ? (
          <p className="text-sm text-gray-400">{partner.give_to_clients ? 'You give your 10% to your clients, so there are no commissions.' : 'No commissions yet.'}</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {data.commissions.map(c => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{new Date(c.earned_at).toLocaleDateString()}</span>
                <span className="text-reset-brown font-medium">{money(c.commission_amount)} <span className="text-xs text-gray-400 font-normal">{c.status === 'paid' ? 'paid' : 'to be paid'}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-8">
        <Kicker>Payout history</Kicker>
        {!data.payouts?.length ? (
          <p className="text-sm text-gray-400">No payouts yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {data.payouts.map((p, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{new Date(p.payout_date).toLocaleDateString()}</span>
                <span className="text-reset-brown font-medium">{money(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-center py-8">
        <p className="font-serif italic text-xl text-reset-terracotta mb-1">Thank you for sharing this with your clients.</p>
        <p className="text-sm text-reset-brown">Questions about anything here? <a href="mailto:team365@365reset.com" className="font-bold underline">Email Jenn</a></p>
      </div>
    </Shell>
  );
}

const IDLE_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const tokenExpiry = (t) => {
  try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp * 1000; } catch { return 0; }
};

export default function PartnerPortal() {
  const [token, setToken] = useState(readToken());
  const [data, setData] = useState(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const lastActivity = useRef(Date.now());

  const signOut = useCallback((why) => {
    saveToken(null); setToken(null); setData(null);
    setNotice(typeof why === 'string' ? why : '');
  }, []);
  const signedIn = useCallback((t) => {
    lastActivity.current = Date.now();
    saveToken(t); setNotice(''); setLinkToken(null); setToken(t);
  }, []);

  // Coming in from the emailed link: choose (or reset) the password.
  useEffect(() => {
    const link = new URLSearchParams(window.location.search).get('setpassword');
    if (!link) return;
    window.history.replaceState(null, '', window.location.pathname);
    signOut();
    setLinkToken(link);
  }, [signOut]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    if (tokenExpiry(token) <= Date.now()) { signOut('You were signed out to keep your clients\' details private. Please sign in again.'); return; }
    setLoading(true);
    partnerApi.get('/partner/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => signOut('Please sign in again.'))
      .finally(() => setLoading(false));
  }, [token, signOut]);

  // 30 minutes with no activity → signed out, and the screen is cleared so
  // client names don't stay showing. While active, the session is renewed
  // (the server stops renewing at the end of the day).
  useEffect(() => {
    if (!token) return undefined;
    const mark = () => { lastActivity.current = Date.now(); };
    const check = () => {
      const now = Date.now();
      if (now - lastActivity.current >= IDLE_MS) {
        signOut('You were signed out after 30 minutes with no activity, to keep your clients\' details private. Please sign in again.');
        return;
      }
      if (tokenExpiry(token) <= now) {
        signOut('You were signed out for the day, to keep your clients\' details private. Please sign in again.');
        return;
      }
      // Active in the last 5 minutes and the session is over 5 minutes old → renew it.
      if (now - lastActivity.current < 5 * 60 * 1000 && tokenExpiry(token) - now < 25 * 60 * 1000) {
        partnerApi.post('/partner/keep-signed-in', {}, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => { saveToken(r.data.token); setToken(r.data.token); })
          .catch(err => { if (err.response?.status === 401) signOut('You were signed out for the day, to keep your clients\' details private. Please sign in again.'); });
      }
    };
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, mark, { passive: true }));
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 60 * 1000);
    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, mark));
      document.removeEventListener('visibilitychange', check);
      clearInterval(timer);
    };
  }, [token, signOut]);

  if (linkToken) return <ChoosePassword linkToken={linkToken} onSignedIn={signedIn} onCancel={() => setLinkToken(null)} />;
  if (loading) return <Shell><p className="text-reset-grey">Loading…</p></Shell>;
  if (!token || !data) return <SignIn notice={notice} onSignedIn={signedIn} />;
  return <Dashboard data={data} setData={setData} token={token} signOut={signOut} />;
}
